import React, { useEffect } from 'react';
import { makeStyles, Box, Typography, Tabs, Tab } from '@material-ui/core';
import { Link } from 'react-router-dom';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Button from '@material-ui/core/Button';
import _get from 'lodash/get';
import PageMeta from 'components/PageMeta';
import Mixpanel from 'utils/mixpanelService';
import { usePreviousLocationContext } from 'components/Routes/PrevLocationContext';
import { CourseSidebarInformation } from './CourseSidebarInformation';
import DashboardIcon from './icons/DashboardIcon';
import InfoIcon from './icons/InfoIcon';
import CourseAbout from './CourseAbout';
import CourseDashboard from './CourseDashboard';

const useStyles = makeStyles(theme => ({
  root: {
    margin: props => (props.isMobile ? theme.spacing(2) : '24px auto 0px'),
    width: props => !props.isMobile && 'max(540px, 60vw)',
  },
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: theme.spacing(2.5),
  },
  heading: {
    marginRight: 8,
    [theme.breakpoints.down('xs')]: {
      marginBottom: 8,
    },
  },
  button: {
    alignSelf: 'flex-start',
    padding: '13px 16px',
    minWidth: 162,
    borderRadius: 8,
    fontWeight: 700,
    marginLeft: props => props.isMobile && 66,

    '& svg': {
      fontSize: theme.typography.body1.fontSize,
      marginLeft: 6,
    },
    [theme.breakpoints.down('md')]: {
      padding: '12px',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '8px',
    },
  },
  tabRoot: {
    textTransform: 'none',
    paddingLeft: 0,
    paddingRight: 5,
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    marginRight: theme.spacing(6),
    minWidth: 'unset',
  },
  indicator: {
    backgroundColor: theme.palette.primary.main,
  },
  flexContainer: {
    borderBottom: '1px solid #E0E0E0',
  },
  link: {
    display: 'flex',
    alignItems: 'center',

    '& p': {
      fontSize: props => props.fontSize,
    },
  },
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`course-intro-tabpanel-${index}`}
    aria-labelledby={`course-intro-tab-${index}`}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </Box>
);

const CourseIntroduction = ({
  course,
  activeModule,
  onEnrollCourse,
  coursesSiteCopy,
  resourceItemPageSiteCopy,
  isEnrolled,
  totalNumberOfModules,
  completedModulesIds,
  completedNumberOfModules,
  completedNumberOfReflections,
  courseProgress,
  removeResourceFromLater,
  takenCourse,
  isCourseCompleted,
  continueCourse,
  onModuleClick,
  getNextResourceIndexes,
  goToResource,
  match,
  pageMeta,
  trackingProps,
  isMobile,
}) => {
  const matchPath = match.params.path;
  const activeTab = matchPath === 'introduction' || !isEnrolled ? 1 : 0;
  const fontSize = isMobile ? '1rem' : '1.25rem';
  const classes = useStyles({
    fontSize,
    isMobile,
  });
  const prevLocation = usePreviousLocationContext();
  const prevPath = _get(prevLocation, 'pathname', '').split('/')[1] || '/';

  useEffect(() => {
    if (!isEnrolled) {
      Mixpanel.track('Course Page - Viewed', {
        course: course.fields.title,
        path: prevPath,
      });
    }
  }, [course, isEnrolled]);

  const handleEnroll = () => {
    onEnrollCourse();
  };

  const handleContinue = (courseModule = activeModule) => {
    Mixpanel.track('Course Dashboard - Continue - Clicked', {
      course: course.fields.title,
      module: _get(courseModule, 'fields.title'),
      path: trackingProps.path,
    });
    continueCourse();
  };

  const onTabChange = (_e, index) => {
    if (index === 1) {
      Mixpanel.track('Course Dashboard - About - Clicked', {
        course: course.fields.title,
        path: trackingProps.path,
      });
    }
  };

  const tabs = [
    {
      title: _get(coursesSiteCopy, 'pageCopy.dashboardTab'),
      Icon: DashboardIcon,
      path: 'dashboard',
    },
    {
      title: _get(coursesSiteCopy, 'pageCopy.aboutTab'),
      Icon: InfoIcon,
      path: 'introduction',
    },
  ];

  const headingVariant = isMobile ? 'h6' : 'h5';

  const enrollButton = !isCourseCompleted && (
    <Button
      className={classes.button}
      variant="contained"
      color="primary"
      onClick={isEnrolled ? handleContinue : handleEnroll}
      size="large"
    >
      {isEnrolled
        ? _get(coursesSiteCopy, 'pageCopy.continue')
        : _get(coursesSiteCopy, 'pageCopy.enroll')}
      <ChevronRightIcon />
    </Button>
  );

  return (
    <Box className={classes.root}>
      <PageMeta type="courses" data={pageMeta} canonicalUrl={match.url} />
      <div className={classes.wrapper}>
        <CourseSidebarInformation
          isMobile={isMobile}
          coursesSiteCopy={coursesSiteCopy}
          item={course}
        />
        {enrollButton}
      </div>
      {isEnrolled && (
        <Box mb={2.5}>
          <Tabs
            value={activeTab}
            classes={{
              indicator: classes.indicator,
              flexContainer: classes.flexContainer,
            }}
            aria-label="course intro tabs"
            onChange={onTabChange}
          >
            {tabs.map((tab, index) => (
              <Tab
                id={`course-intro-tab-${index}`}
                aria-controls={`course-intro-tabpanel-${index}`}
                key={tab.title}
                className={classes.tabRoot}
                label={
                  <Link to={tab.path} className={classes.link}>
                    <Box mr={2.5}>
                      <tab.Icon />
                    </Box>
                    <Typography>{tab.title}</Typography>
                  </Link>
                }
                disableRipple
                disableFocusRipple
              />
            ))}
          </Tabs>
        </Box>
      )}

      <TabPanel value={activeTab} index={0}>
        <CourseDashboard
          isMobile={isMobile}
          headingVariant={headingVariant}
          continueCourse={handleContinue}
          totalNumberOfModules={totalNumberOfModules}
          completedNumberOfModules={completedNumberOfModules}
          completedNumberOfReflections={completedNumberOfReflections}
          courseProgress={courseProgress}
          removeResourceFromLater={removeResourceFromLater}
          takenCourse={takenCourse}
          isCourseCompleted={isCourseCompleted}
          coursesSiteCopy={coursesSiteCopy}
          completedModulesIds={completedModulesIds}
          course={course}
          onModuleClick={onModuleClick}
          getNextResourceIndexes={getNextResourceIndexes}
          goToResource={goToResource}
          trackingProps={trackingProps}
          prevPath={prevPath}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <CourseAbout
          isMobile={isMobile}
          fontSize={fontSize}
          headingVariant={headingVariant}
          course={course}
          resourceItemPageSiteCopy={resourceItemPageSiteCopy}
          coursesSiteCopy={coursesSiteCopy}
          enrollButton={enrollButton}
        />
      </TabPanel>
    </Box>
  );
};

export default CourseIntroduction;
